<template>
    <v-container v-if="application" fluid>
        <v-card class="mb-3">
            <v-card-title>
                <v-row align="center">
                    <v-col cols="12" md="8">
                        Team application #{{ application.id }}
                        <v-chip class="ml-3" small dark :color="statusColors[application.status]">{{ application.status }}</v-chip>
                    </v-col>
                    <v-col cols="12" md="4" align="end">
                        <v-btn class="my-2 mr-2" small outlined @click="$router.push('/team-applications')">
                            <v-icon left>mdi-arrow-left</v-icon>Back
                        </v-btn>
                    </v-col>
                </v-row>
            </v-card-title>
            <v-card-text v-if="!isPending">
                <v-alert dense text :type="application.status === 'accepted' ? 'success' : 'info'" class="mb-0">
                    This application was {{ application.status }} and can no longer be changed.
                </v-alert>
            </v-card-text>
        </v-card>

        <v-card class="mb-3">
            <v-card-title class="text-subtitle-1 font-weight-bold">Team</v-card-title>
            <v-card-text>
                <v-row align="center">
                    <v-col v-if="application.teamLogo" cols="12" md="2">
                        <v-img :src="`${googleApiHost}/${application.teamLogo}`" max-height="90" contain />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-text-field v-model="team.name" label="Name *" :disabled="!isPending" :error="!team.name" hide-details="auto" />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-text-field v-model="team.nickName" label="Nickname" :disabled="!isPending" hide-details />
                    </v-col>
                    <v-col cols="12" sm="6" md="2">
                        <v-autocomplete
                            v-model="team.leagueId"
                            :items="leagues"
                            item-text="title"
                            item-value="id"
                            label="League"
                            :disabled="!isPending"
                            clearable
                            hide-details
                        />
                    </v-col>
                    <v-col cols="12" sm="6" md="2">
                        <v-text-field v-model="team.color" label="Color" type="color" :disabled="!isPending" hide-details />
                    </v-col>
                    <v-col cols="12" sm="6" md="2">
                        <v-text-field v-model="team.brandColor" label="Match Color" type="color" :disabled="!isPending" hide-details />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-text-field :value="application.telephone" label="Contact telephone" readonly hide-details />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-text-field :value="application.email" label="Contact email" readonly hide-details />
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-switch v-model="team.active" :label="team.active ? 'Active' : 'Not Active'" :disabled="!isPending" hide-details />
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>

        <v-card class="mb-3">
            <v-card-title class="text-subtitle-1 font-weight-bold">
                Players ({{ includedRows.length }} of {{ rows.length }} included)
            </v-card-title>
            <v-card-text>
                <v-data-table
                    :headers="playerHeaders"
                    :items="rows"
                    item-key="key"
                    :loading="matchesLoading"
                    disable-pagination
                    disable-sort
                    hide-default-footer
                    dense
                    :mobile-breakpoint="0"
                    class="players-table"
                >
                    <template v-slot:item="{ item }">
                        <tr :class="{ 'excluded-row': !item.include }">
                            <td>
                                <v-simple-checkbox v-model="item.include" :disabled="!isPending" />
                            </td>
                            <td :class="{ 'match-cell': hasMatches }">
                                <v-select
                                    v-if="item.matches.length"
                                    v-model="item.existingId"
                                    :items="matchOptions(item)"
                                    :disabled="!isPending || !item.include"
                                    dense
                                    hide-details
                                >
                                    <template v-slot:prepend>
                                        <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-icon color="orange" small v-bind="attrs" v-on="on">mdi-account-alert</v-icon>
                                            </template>
                                            <span>A player with the same name already exists. Pick them to move them to this team instead of creating a duplicate.</span>
                                        </v-tooltip>
                                    </template>
                                </v-select>
                                <span v-else class="grey--text text-caption">New player</span>
                            </td>
                            <td v-for="field in editableFields" :key="field.value" :style="{ minWidth: field.width + 'px' }">
                                <v-select
                                    v-if="field.items"
                                    v-model="item[field.value]"
                                    :items="field.items"
                                    :disabled="isFieldDisabled(item, field)"
                                    dense
                                    hide-details
                                    :clearable="!field.required"
                                />
                                <v-text-field
                                    v-else
                                    v-model="item[field.value]"
                                    :type="field.type || 'text'"
                                    :disabled="isFieldDisabled(item, field)"
                                    :error="field.required && item.include && !item.existingId && !item[field.value]"
                                    dense
                                    hide-details
                                />
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <v-alert v-if="isPending && validationErrors.length" dense text type="warning">
            <div v-for="validationError in validationErrors" :key="validationError">{{ validationError }}</div>
        </v-alert>
        <v-alert v-if="errorMessage" dense text type="error" dismissible @input="errorMessage = null">
            {{ errorMessage }}
        </v-alert>

        <v-row v-if="isPending">
            <v-col cols="12" align="end">
                <v-btn class="mx-2 white--text" large color="red" :disabled="saving" @click="rejectDialog = true">
                    <v-icon left>mdi-close</v-icon>Reject
                </v-btn>
                <v-btn class="mx-2 white--text" large color="black" :loading="saving" :disabled="validationErrors.length > 0" @click="approve">
                    <v-icon left>mdi-check</v-icon>Approve & create team
                </v-btn>
            </v-col>
        </v-row>

        <v-dialog v-model="rejectDialog" max-width="420">
            <v-card>
                <v-card-title>Reject application?</v-card-title>
                <v-card-text>{{ application.teamName }} will be marked as rejected. No team or players are created.</v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="rejectDialog = false">Cancel</v-btn>
                    <v-btn color="red" dark :loading="saving" @click="reject">Reject</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
    import teamApplicationService from '@/services/team-application'
    import leagueService from '@/services/league'
    import playerService from '@/services/player'

    const NO_OF_MATCHES_ITEMS = [
        { text: '#MatchesRule1', value: 'noOfMatches1' },
        { text: '#MatchesRule2', value: 'noOfMatches2' },
        { text: '#MatchesRule3', value: 'noOfMatches3' }
    ]

    // the public registration form collects Greek mobiles without a country code, which the backend rejects
    function normalizeMobile(mobile) {
        if (!mobile) {
            return null
        }
        const digits = String(mobile).replace(/[\s-]/g, '')
        if (/^69\d{8}$/.test(digits)) {
            return `+30${digits}`
        }
        if (/^30\d{10}$/.test(digits)) {
            return `+${digits}`
        }
        return digits
    }

    function nameKey(name, surname) {
        return `${(name || '').trim().toLowerCase()}|${(surname || '').trim().toLowerCase()}`
    }

    export default {
        name: 'TeamApplicationForm',
        data: () => ({
            application: null,
            team: { name: null, nickName: null, leagueId: null, color: '#000000', brandColor: '#000000', active: true },
            rows: [],
            leagues: [],
            matchesLoading: false,
            saving: false,
            errorMessage: null,
            rejectDialog: false,
            googleApiHost: 'https://storage.googleapis.com',
            statusColors: { pending: 'orange', accepted: 'green', rejected: 'red' },
            editableFields: [
                { text: 'Name *', value: 'name', required: true, width: 120 },
                { text: 'Surname', value: 'surname', width: 140 },
                { text: 'Birth Year', value: 'birthdayYear', type: 'number', width: 80 },
                { text: 'Mobile', value: 'mobile', width: 130 },
                { text: 'Email', value: 'email', width: 180 },
                { text: 'Jersey', value: 'jersey', type: 'number', appliesToExisting: true, width: 60 },
                { text: 'Position', value: 'position', items: ['guard', 'forward', 'center'], width: 115 },
                { text: 'Height', value: 'height', type: 'number', width: 65 },
                { text: 'Residence', value: 'residence', width: 120 },
                { text: '#MatchesRule', value: 'noOfMatches', items: NO_OF_MATCHES_ITEMS, required: true, width: 140 }
            ]
        }),
        computed: {
            isPending() {
                return this.application && this.application.status === 'pending'
            },
            playerHeaders() {
                return [
                    { text: 'Include', value: 'include', width: 1 },
                    { text: 'Existing player?', value: 'existingId', width: this.hasMatches ? 240 : 1 }
                ].concat(this.editableFields.map(field => ({ text: field.text, value: field.value, width: field.width })))
            },
            hasMatches() {
                return this.rows.some(row => row.matches.length)
            },
            includedRows() {
                return this.rows.filter(row => row.include)
            },
            validationErrors() {
                const errors = []
                if (!this.team.name || !this.team.name.trim()) {
                    errors.push('The team needs a name.')
                }
                this.includedRows.forEach((row, index) => {
                    if (!row.existingId && !row.name) {
                        errors.push(`Player ${index + 1} needs a name.`)
                    }
                })
                const jerseys = this.includedRows.map(row => row.jersey).filter(jersey => jersey !== null && jersey !== '')
                const duplicates = [...new Set(jerseys.filter((jersey, index) => jerseys.indexOf(jersey) !== index))]
                if (duplicates.length) {
                    errors.push(`Jersey ${duplicates.join(', ')} is used by more than one player.`)
                }
                const existingIds = this.includedRows.map(row => row.existingId).filter(id => id)
                if (new Set(existingIds).size !== existingIds.length) {
                    errors.push('The same existing player is selected for more than one row.')
                }
                return errors
            }
        },
        created() {
            leagueService.getAllLeagues().then(resp => {
                this.leagues = resp.data
            })
            this.fetchTeamApplication()
        },
        methods: {
            fetchTeamApplication() {
                teamApplicationService.getTeamApplication(this.$route.params.tAId).then(resp => {
                    this.application = resp.data
                    this.team.name = this.application.teamName
                    this.team.color = this.application.color || '#000000'
                    this.team.brandColor = this.application.brandColor || '#000000'
                    this.rows = (this.application.players || []).map((player, index) => ({
                        key: index,
                        include: true,
                        existingId: null,
                        matches: [],
                        name: player.name,
                        // the public form initialises `surName` but binds `surname`
                        surname: player.surname || player.surName,
                        // the public form sends a full date, only its year is kept
                        birthdayYear: player.birthdayYear || (player.birthday ? Number(String(player.birthday).slice(0, 4)) || null : null),
                        mobile: normalizeMobile(player.mobile),
                        email: player.email || null,
                        jersey: player.jersey,
                        position: player.position,
                        height: player.height,
                        residence: player.residence,
                        noOfMatches: 'noOfMatches1'
                    }))
                    if (this.isPending) {
                        this.findExistingPlayers()
                    }
                })
            },
            // one request for all players: an array filter is OR-ed by the backend
            findExistingPlayers() {
                const filter = this.rows
                    .filter(row => row.name)
                    .map(row => ({ name: row.name.trim(), surname: (row.surname || '').trim() }))
                if (!filter.length) {
                    return
                }
                this.matchesLoading = true
                playerService.getFilteredPlayers(filter).then(resp => {
                    const byName = {}
                    resp.data.content.forEach(player => {
                        const key = nameKey(player.name, player.surname)
                        byName[key] = (byName[key] || []).concat(player)
                    })
                    this.rows.forEach(row => {
                        row.matches = byName[nameKey(row.name, row.surname)] || []
                    })
                }).finally(() => {
                    this.matchesLoading = false
                })
            },
            matchOptions(row) {
                return [{ text: 'Create new player', value: null }].concat(row.matches.map(player => ({
                    text: `Use #${player.id} ${player.name} ${player.surname || ''}` +
                        (player.birthdayYear ? ` · ${player.birthdayYear}` : '') +
                        (player.team ? ` · ${player.team.name}` : ''),
                    value: player.id
                })))
            },
            isFieldDisabled(row, field) {
                return !this.isPending || !row.include || (row.existingId && !field.appliesToExisting)
            },
            toNumber(value) {
                return value === null || value === undefined || value === '' ? null : Number(value)
            },
            buildPayload() {
                return {
                    team: {
                        name: this.team.name.trim(),
                        nickName: this.team.nickName,
                        color: this.team.color,
                        brandColor: this.team.brandColor,
                        logo: this.application.teamLogo,
                        active: this.team.active,
                        league: this.team.leagueId ? { id: this.team.leagueId } : null
                    },
                    players: this.includedRows.map(row => {
                        if (row.existingId) {
                            return { id: row.existingId, jersey: this.toNumber(row.jersey), active: true }
                        }
                        return {
                            id: 0,
                            name: row.name.trim(),
                            surname: (row.surname || '').trim() || null,
                            birthdayYear: this.toNumber(row.birthdayYear),
                            mobile: row.mobile || null,
                            email: row.email ? row.email.trim() : null,
                            jersey: this.toNumber(row.jersey),
                            position: row.position || null,
                            height: this.toNumber(row.height),
                            residence: row.residence || null,
                            noOfMatches: row.noOfMatches,
                            active: true
                        }
                    })
                }
            },
            approve() {
                this.saving = true
                this.errorMessage = null
                teamApplicationService.approveTeamApplication(this.application.id, this.buildPayload()).then(resp => {
                    this.$root.$emit('requests-changed')
                    this.$router.push(`/teams/${resp.data.content}/edit`)
                }).catch(error => {
                    this.errorMessage = this.getErrorMessage(error)
                }).finally(() => {
                    this.saving = false
                })
            },
            reject() {
                this.saving = true
                this.errorMessage = null
                teamApplicationService.rejectTeamApplication(this.application.id).then(() => {
                    this.$root.$emit('requests-changed')
                    this.$router.push('/team-applications')
                }).catch(error => {
                    this.errorMessage = this.getErrorMessage(error)
                }).finally(() => {
                    this.saving = false
                    this.rejectDialog = false
                })
            },
            getErrorMessage(error) {
                return (error.response && error.response.data && error.response.data.message) || 'Something went wrong, please try again.'
            }
        }
    }
</script>

<style scoped>
    .excluded-row {
        opacity: 0.45;
    }

    .match-cell {
        min-width: 240px;
    }

    .players-table ::v-deep th,
    .players-table ::v-deep td {
        padding: 0 6px !important;
        white-space: nowrap;
    }
</style>
